import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * LLM token spend for the tenant — `GET /v1/llm-token-usage/summary`.
 *
 * Figures are **lifetime**: the endpoint takes no date range, so everything here is all-time.
 *
 * `feature` arrives as the backend's raw enum value (`chat_turn`, `nugget_graph_run`, …) and is
 * mapped to a label in the UI rather than by the API — a new backend feature then renders as its
 * raw key instead of breaking, and needs no coordinated release to display.
 *
 * Reading it requires the Administrator-only `token-usage-view`, so callers outside Settings must
 * pass `enabled: false` when the user lacks it — otherwise every mount is a wasted 403. Mirrors the
 * `enabled` option on `useLlmApiKeys`.
 */
export type TokenUsageTotals = {
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  reasoning_tokens: number;
  call_count: number;
  image_count: number;
  run_count: number;
};

export type TokenUsageByFeature = {
  feature: string;
  /** `input_tokens + output_tokens`. */
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  run_count: number;
  /**
   * Which models this feature's spend went to, biggest first — the drilldown under its row.
   *
   * Arrives with the summary rather than on expand, so opening a row costs no request. Optional
   * because a backend that predates the field must still validate; treat a missing value as `[]`.
   *
   * These need not sum to `total_tokens`: that comes from the runs' own columns, which include runs
   * whose per-model breakdown was never recorded.
   */
  by_model?: TokenUsageByModel[];
};

export type TokenUsageByModel = {
  model_id: string;
  /** `input_tokens + output_tokens`. */
  total_tokens: number;
  input_tokens: number;
  /** Always 0 for embedding models — nothing is generated. */
  output_tokens: number;
  /** Provider requests, not runs — one run can span several models, so these do not sum to runs. */
  call_count: number;
};

export type TokenUsageSummary = {
  totals: TokenUsageTotals;
  by_feature: TokenUsageByFeature[];
  by_model: TokenUsageByModel[];
};

export type TokenUsageState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  summary: TokenUsageSummary | null;
};

export const tokenUsageInitialState: TokenUsageState = {
  status: STATUS.IDLE,
  error: null,
  summary: null,
};

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isTotals(value: unknown): value is TokenUsageTotals {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    isNumber(row.total_tokens) &&
    isNumber(row.input_tokens) &&
    isNumber(row.output_tokens) &&
    isNumber(row.cached_input_tokens) &&
    isNumber(row.reasoning_tokens) &&
    isNumber(row.call_count) &&
    isNumber(row.image_count) &&
    isNumber(row.run_count)
  );
}

function isByFeature(value: unknown): value is TokenUsageByFeature {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.feature === "string" &&
    isNumber(row.total_tokens) &&
    isNumber(row.input_tokens) &&
    isNumber(row.output_tokens) &&
    isNumber(row.run_count) &&
    // Absent is valid — the field was added after the endpoint shipped, so a stale backend must not
    // fail validation and blank the whole tab. Present but malformed is still rejected.
    (row.by_model === undefined ||
      (Array.isArray(row.by_model) && row.by_model.every(isByModel)))
  );
}

function isByModel(value: unknown): value is TokenUsageByModel {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.model_id === "string" &&
    isNumber(row.total_tokens) &&
    isNumber(row.input_tokens) &&
    isNumber(row.output_tokens) &&
    isNumber(row.call_count)
  );
}

function isTokenUsageSummary(value: unknown): value is TokenUsageSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    isTotals(row.totals) &&
    Array.isArray(row.by_feature) &&
    row.by_feature.every(isByFeature) &&
    Array.isArray(row.by_model) &&
    row.by_model.every(isByModel)
  );
}

export const fetchTokenUsage = createAsyncThunk<TokenUsageSummary, void, { rejectValue: string }>(
  "tokenUsage/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/llm-token-usage/summary", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isTokenUsageSummary(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

const tokenUsageSlice = createSlice({
  name: "tokenUsage",
  initialState: tokenUsageInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTokenUsage.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTokenUsage.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.summary = action.payload;
      })
      .addCase(fetchTokenUsage.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      });
  },
});

export const tokenUsageReducer = tokenUsageSlice.reducer;
